import { type TabsConfig, useTabsConfig } from '@/components/opportunity/useTabsConfig'
import { PLACEHOLDER_ACTIONS_DETAILS } from '@/constants/placeholders'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepName } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { useEffect, useRef, useState } from 'react'
import type { Address } from 'viem'
import { BottomPanel } from '../bottomPanel/bottomPanel'
import { SidePanel } from '../sidePanel/sidePanel'

interface TxPanelProps {
  vault: Vault
  walletAddress: Address | undefined
  transactionsContext: TransactionsContext
  isContextError: boolean
  isContextFetching: boolean
  onRetryContext: () => void
}

export interface TxPanelSharedProps {
  vault: Vault
  walletAddress: Address | undefined
  transactionsContext: TransactionsContext
  tabValue: StepName
  setTabValue: (value: StepName) => void
  tabsConfig: TabsConfig
  positionValue: bigint
  parsedAmount: bigint
  totalPositionValue: bigint
  isContainRewards: boolean
  isContextError: boolean
  isContextFetching: boolean
  onRetryContext: () => void
}

export const TxPanel = ({
  vault,
  walletAddress,
  transactionsContext,
  isContextError,
  isContextFetching,
  onRetryContext,
}: TxPanelProps) => {
  const safeContext = transactionsContext ?? PLACEHOLDER_ACTIONS_DETAILS

  const [selectedTab, setTabValue] = useState<StepName>()
  const tabValue = selectedTab ?? safeContext.allSteps[0]?.name ?? 'deposit'
  const tabsConfig = useTabsConfig({ vault, transactionsContext: safeContext })
  const previousWallet = useRef(walletAddress?.toLowerCase())

  useEffect(() => {
    const address = walletAddress?.toLowerCase()
    if (previousWallet.current === address) return
    previousWallet.current = address

    for (const { form } of Object.values(tabsConfig)) {
      form.reset(undefined, { keepValues: true })
    }
  }, [walletAddress, tabsConfig])

  const activeConfig = tabsConfig[tabValue]
  const amount = activeConfig.form.watch('amount') || '0'

  const positionValue = BigInt(safeContext.asset.positionValueInAsset ?? 0)
  const parsedAmount = BigInt(activeConfig.getParsedAssetAmount(amount) ?? 0)

  const childrenValue =
    safeContext.childrenPositions?.reduce((acc, child) => acc + BigInt(child.asset.positionValueInAsset ?? 0), 0n) ?? 0n
  const totalPositionValue = positionValue + childrenValue

  const isContainRewards = vault.rewards.length > 0

  const sharedProps: TxPanelSharedProps = {
    vault,
    walletAddress,
    transactionsContext,
    tabValue,
    setTabValue,
    tabsConfig,
    positionValue,
    parsedAmount,
    totalPositionValue,
    isContainRewards,
    isContextError,
    isContextFetching,
    onRetryContext,
  }

  return (
    <>
      <SidePanel className={cn('sticky top-6 max-w-96 overflow-hidden', 'hidden lg:flex')} {...sharedProps} />
      <BottomPanel className={cn('p-4', 'fixed right-0 bottom-0 left-0 z-50', 'block', 'lg:hidden')} {...sharedProps} />
    </>
  )
}
